import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import CustomerHeader from "@/components/CustomerHeader";
import { Coins, Crown, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";

const CURRENCY_LABEL = { try: "₺", usd: "$", eur: "€" };

export default function Store() {
  const [tokens, setTokens] = useState([]);
  const [plans, setPlans] = useState([]);
  const [buyLoading, setBuyLoading] = useState("");
  const [currency, setCurrency] = useState(null);
  const [showSwitch, setShowSwitch] = useState(false);

  useEffect(() => {
    api.get("/packages/tokens").then(({ data }) => setTokens(data));
    api.get("/packages/premium").then(({ data }) => setPlans(data));
    const saved = localStorage.getItem("falzilla_currency");
    if (saved) {
      setCurrency(saved);
    } else {
      api.get("/geo").then(({ data }) => {
        setCurrency(data.currency || "usd");
        localStorage.setItem("falzilla_currency", data.currency || "usd");
      }).catch(() => setCurrency("usd"));
    }
  }, []);

  const changeCurrency = (c) => { setCurrency(c); localStorage.setItem("falzilla_currency", c); };

  const priceOf = (p) =>
    currency === "try" ? Math.round(p.price_try) + "₺"
    : currency === "eur" ? "€" + ((p.price_eur || p.price_usd) || 0).toFixed(2)
    : "$" + (p.price_usd || 0).toFixed(2);

  const buyTokens = async (slug) => {
    setBuyLoading("t-" + slug);
    try {
      const { data } = await api.post("/payments/tokens/checkout", { package_slug: slug, currency, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) { toast.error(err.response?.data?.detail || "Ödeme başlatılamadı"); setBuyLoading(""); }
  };

  const buyPremium = async (slug) => {
    setBuyLoading("p-" + slug);
    try {
      const { data } = await api.post("/payments/premium/checkout", { plan_slug: slug, currency, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) { toast.error(err.response?.data?.detail || "Ödeme başlatılamadı"); setBuyLoading(""); }
  };

  return (
    <div className="cosmic-bg min-h-screen">
      <div className="stars" />
      <CustomerHeader />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <h1 className="font-serif-myst text-4xl md:text-5xl mb-2">Mağaza</h1>
        <p className="text-white/60 mb-6">Jeton al veya Premium'a geç. Fiyatlar bulunduğun ülkeye göre <button data-testid="currency-toggle" onClick={() => setShowSwitch(!showSwitch)} className="gold-text underline">{CURRENCY_LABEL[currency] || "$"}</button> olarak gösteriliyor.</p>

        {showSwitch && (
          <div className="glass rounded-full p-1 inline-flex mb-8" data-testid="currency-switch">
            {["try", "usd", "eur"].map((c) => (
              <button key={c} onClick={() => changeCurrency(c)} data-testid={`currency-${c}`}
                className={`px-4 py-1.5 rounded-full text-sm transition-colors ${currency === c ? "btn-gold" : "text-white/60 hover:text-white"}`}>
                {c === "try" ? "₺ TL" : c === "usd" ? "$ USD" : "€ EUR"}
              </button>
            ))}
          </div>
        )}
        {!currency && <div className="text-white/50">Yükleniyor...</div>}

        <section className="mb-14">
          <h2 className="font-serif-myst text-2xl mb-5 flex items-center gap-2"><Coins className="w-6 h-6 gold-text" /> Jeton Paketleri</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="token-packages-grid">
            {tokens.map((p) => (
              <div key={p.id} data-testid={`token-pkg-${p.slug}`} className={`glass rounded-2xl p-5 border ${p.is_popular ? "gold-border" : "border-white/5"} relative`}>
                {p.is_popular && <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 token-pill px-2.5 py-0.5 rounded-full text-[10px] uppercase tracking-wider">En Popüler</div>}
                <div className="text-sm text-white/60">{p.name}</div>
                <div className="font-serif-myst text-4xl gold-text mt-1">{p.tokens}<span className="text-base text-white/50 ml-1">jeton</span></div>
                {p.bonus_tokens > 0 && <div className="text-xs text-emerald-300 mt-1">+{p.bonus_tokens} bonus</div>}
                <div className="text-2xl font-semibold mt-4">{priceOf(p)}</div>
                <button onClick={() => buyTokens(p.slug)} disabled={buyLoading === "t-" + p.slug} data-testid={`buy-token-${p.slug}`} className="btn-gold w-full py-2.5 rounded-full mt-4 text-sm flex items-center justify-center gap-2">
                  {buyLoading === "t-" + p.slug ? <Loader2 className="w-4 h-4 animate-spin" /> : "Satın Al"}
                </button>
              </div>
            ))}
          </div>
        </section>

        <section>
          <h2 className="font-serif-myst text-2xl mb-5 flex items-center gap-2"><Crown className="w-6 h-6 gold-text" /> Premium Üyelik</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="premium-plans-grid">
            {plans.map((p) => (
              <div key={p.id} data-testid={`plan-${p.slug}`} className="glass rounded-2xl p-6 gold-border">
                <div className="flex items-baseline justify-between">
                  <div>
                    <div className="font-serif-myst text-2xl">{p.name}</div>
                    <div className="text-xs text-white/50">{p.duration_days} gün</div>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-semibold">{priceOf(p)}</div>
                  </div>
                </div>
                <ul className="mt-4 space-y-2 text-sm">
                  {(p.features || []).map((f, i) => (
                    <li key={i} className="flex items-start gap-2"><Check className="w-4 h-4 gold-text mt-0.5 shrink-0" /><span>{f}</span></li>
                  ))}
                </ul>
                <button onClick={() => buyPremium(p.slug)} disabled={buyLoading === "p-" + p.slug} data-testid={`buy-plan-${p.slug}`} className="btn-gold w-full py-3 rounded-full mt-6 flex items-center justify-center gap-2">
                  {buyLoading === "p-" + p.slug ? <Loader2 className="w-4 h-4 animate-spin" /> : "Premium'a Geç"}
                </button>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}
